/* eslint-disable react/prop-types */
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { GET_SESSIONS_LIST, setPageNumber } from './ground-truth-updates.reducer';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Button } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { apiUpdateGroundTruthVerifiedFlag } from './ground-truth-updates.api';
import SessionInfoCard from './session-info-card/session-info-card';
import { isAdminUserAccount } from '../../helpers/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

function buildCards({
  sessions,
  setActiveSessionId,
  handleDialogOpen,
  showGroundTruthCorrectionDetails,
  adminUser
}) {
  if (!sessions || !Array.isArray(sessions)) {
    return [];
  }

  const handleGroundTruthVerifiedFlagChange = (event) => {
    const sessionId = event.target.name;
    setActiveSessionId(sessionId);
    handleDialogOpen();
  };

  return sessions
    .filter((session) => {
      return (!adminUser && !session?.ground_truth_verified_timestamp) || (adminUser);
    })
    .map((session) => (
    <SessionInfoCard
      key={session.session_id}
      session={session}
      handleGroundTruthVerifiedFlagChange={handleGroundTruthVerifiedFlagChange}
      expandedDetails={showGroundTruthCorrectionDetails}
      adminUser={adminUser}
    />
    ));
}

const Pagination = ({ pageNumber, handlePrevPage, handleNextPage, sessions, pageSize }) => (
  <Box
    width={'40%'}
    height="3rem"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center', marginRight: '1.5rem' }}>
      Page {pageNumber}
    </Typography>
    <Button
      variant="outlined"
      onClick={handlePrevPage}
      disabled={pageNumber === 1}
      sx={{ mr: 2 }}
    >
      Previous Page
    </Button>
    <Button
      variant="outlined"
      onClick={handleNextPage}
      disabled={sessions.length < pageSize}
    >
      Next Page
    </Button>
  </Box>
);

Pagination.propTypes = {
  pageNumber: PropTypes.number.isRequired,
  handlePrevPage: PropTypes.func.isRequired,
  handleNextPage: PropTypes.func.isRequired,
  sessions: PropTypes.array.isRequired,
  pageSize: PropTypes.number.isRequired,
};

const GroundTruthUpdatesHome = () => {
  const dispatch = useDispatch();
  const [activeSessionId, setActiveSessionId] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [showGroundTruthCorrectionDetails, setShowGroundTruthCorrectionDetails] = React.useState(false);
  const {
    sessions, actionInProgress, pageNumber,
  } = useSelector((state) => state.groundTruthUpdates);
  const { sessionDetails } = useSelector((state) => state.auth);
  const adminUser = isAdminUserAccount(sessionDetails);

  const PAGE_SIZE = 20;
  const [pageSize] = React.useState(PAGE_SIZE);

  const handleSwitchChange = (event) => {
    setShowGroundTruthCorrectionDetails(event.target.checked);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setActiveSessionId(null);
  };

  // Fetch sessions data when intervalDays or pageNumber changes
  useEffect(() => {
    dispatch({
      type: GET_SESSIONS_LIST,
      payload: {
        page: pageNumber,
      },
    });

    const timer = setInterval(() => {
      dispatch({
        type: GET_SESSIONS_LIST,
        payload: {
          page: pageNumber,
        },
      });
    }, 30000);

    return () => {
      clearInterval(timer);
    };
  }, [pageNumber, pageSize, dispatch]);

  const cards = buildCards({
    sessions,
    setActiveSessionId,
    handleDialogOpen,
    showGroundTruthCorrectionDetails,
    adminUser
  });

  const handleNextPage = () => {
    const nextPage = pageNumber + 1;
    dispatch(setPageNumber({ pageNumber: nextPage }));
  };
  const handlePrevPage = () => {
    const nextPage = Math.max(1, pageNumber - 1);
    dispatch(setPageNumber({ pageNumber: nextPage }));
  };

  const submitGroundTruthVerificationStatus = () => {
    apiUpdateGroundTruthVerifiedFlag({ sessionId: activeSessionId })
      .then(() => {
        dispatch({
          type: GET_SESSIONS_LIST,
          payload: {
            page: pageNumber,
          },
        });
        handleDialogClose();
        dispatch({
          type: 'SHOW_NOTIFICATION',
          payload: {
            message: `Successfully updated ground truth verification status for session ${activeSessionId}.`,
            severity: 'success',
            autoHideDuration: 3000,
          },
        });
      })
      .catch((error) => {
        console.error('Error updating ground truth verification status:', error);
        dispatch({
          type: 'SHOW_NOTIFICATION',
          payload: {
            message: `Failed to update ground truth verification status for session ${activeSessionId}.`,
            severity: 'error',
            autoHideDuration: 3000,
          },
        });
      });
  };

  return (<Stack direction="column" spacing={2} mb="2rem"
                 sx={{ width: '100%' }} justifyContent="flex-end">
    <Box
      sx={{
        textAlign: 'center', marginBottom: '2rem'
      }}
    >
      <h1>Ground Truth Updates: Sessions</h1>
    </Box>
    <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
      <Box
        width={'30%'}
        height="3rem"
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
      >
        {actionInProgress ? (<>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Alert severity="info"> {'Fetching sessions list...'}</Alert>
          </Stack>
        </>) : null}
      </Box>
      {
        adminUser ? (
          <Box
            width={'30%'}
            height="3rem"
            marginRight="1rem"
            display="flex"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={showGroundTruthCorrectionDetails} onChange={handleSwitchChange}/>}
                label="Show ground truth correction details"
              />
            </FormGroup>
          </Box>
        ) : null
      }
      <Pagination
        pageNumber={pageNumber}
        handlePrevPage={handlePrevPage}
        handleNextPage={handleNextPage}
        sessions={sessions}
        pageSize={pageSize}
      />
    </Stack>
    <Box sx={{
      flexGrow: 1, display: 'flex',
    }}
    >
      {sessions.length > 0 ? (<Grid container spacing={2}>
        {cards}
      </Grid>) : (<Typography variant="h5" component="div" marginY={'3rem'}>
        {'No sessions available for the selected interval.'}
      </Typography>)}
    </Box>
    {
      sessions.length > 6 ? (
        <Stack direction="row" width="100%" justifyContent="flex-end" alignItems="center">
          <Pagination
            pageNumber={pageNumber}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
            sessions={sessions}
            pageSize={pageSize}
          />
        </Stack>
      ) : null
    }
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {`Change session ${activeSessionId}'s ground truth verification status`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {`Are you sure you want to change the ground truth verification 
          status for this session ${activeSessionId}?`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>No</Button>
        <Button variant="contained" onClick={submitGroundTruthVerificationStatus} autoFocus>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  </Stack>);
};

export default React.memo(GroundTruthUpdatesHome);
